import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useWallet } from "@/context/WalletContext";
import { createApiService, apiService } from "@/App";
import { toast } from "sonner";
import Header from "@/components/Header";
import { 
  Bot, 
  ArrowLeft,
  Settings,
  Users,
  TrendingUp,
  Calendar,
  Copy,
  Edit,
  Check,
  X,
  Camera,
  Loader2
} from "lucide-react";
import SwapSTXOSHIButton from "@/components/SwapSTXOSHIButton";
import WalletHealth from "@/components/WalletHealth";
import DepositRecovery from "@/components/DepositRecovery";
import UserWalletPanel from "@/components/UserWalletPanel";
import ProfilePostCard from "@/components/ProfilePostCard";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Compact P&L formatter — turns large STXOSHI amounts into K/M/B suffixed
 * strings so the profile stats row fits inside an iPhone viewport without
 * horizontal scrolling. Under 1,000 shows two decimals.
 */
function formatCompactPnl(v) {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_000_000_000) return `${sign}${(abs / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000)     return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000)         return `${sign}${(abs / 1_000).toFixed(2)}K`;
  return `${sign}${abs.toFixed(2)}`;
}

export default function Profile() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isConnected, user: currentUser, addresses, getAuthHeaders, updateUser, refreshUser } = useWallet();
  const authenticatedApi = createApiService(getAuthHeaders);
  const fileInputRef = useRef(null);
  
  const [profile, setProfile] = useState(null);
  const [agents, setAgents] = useState([]);
  const [bots, setBots] = useState([]);
  const [posts, setPosts] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({ username: '', bio: '' });
  const [activeTab, setActiveTab] = useState('posts');
  const tabsAnchorRef = useRef(null);
  // Switch the profile tab AND scroll it into view. Without the scroll the tab
  // content sits far below the fold on mobile, so tapping a stat (e.g. Agents)
  // looked like a dead button even though the tab had changed.
  const goToTab = (tab) => {
    setActiveTab(tab);
    requestAnimationFrame(() => {
      tabsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [pnlSummary, setPnlSummary] = useState(null);

  const isOwnProfile = currentUser?.id === id;

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProfile = async () => {
    try {
      const [profileRes, postsRes] = await Promise.all([
        apiService.getUser(id),
        apiService.getUserPosts(id)
      ]);
      setProfile(profileRes.data);
      setPosts(postsRes.data);
      setEditData({ 
        username: profileRes.data.username || '', 
        bio: profileRes.data.bio || '' 
      });
      
      // Get user's agents (for any profile, not just own)
      try {
        const agentsRes = await fetch(`${API}/users/${id}/agents`);
        if (agentsRes.ok) {
          const agentsData = await agentsRes.json();
          
          // Fetch positions for each agent to get yield and position data
          const agentsWithPositions = await Promise.all(
            agentsData.map(async (agent) => {
              try {
                const positionsRes = await fetch(
                  `${process.env.REACT_APP_BACKEND_URL}/api/agents/${agent.id}/defi/positions`
                );
                if (positionsRes.ok) {
                  const posData = await positionsRes.json();
                  const yieldEarned = posData.positions?.reduce((total, pos) => total + (pos.yield_earned || 0), 0) || 0;
                  return {
                    ...agent,
                    active_positions_count: posData.summary?.active_positions || 0,
                    yield_earned: yieldEarned,
                    total_yield: agent.total_yield_earned || yieldEarned,
                    // STXOSHI figures the agent was funded/earned in (doc fields;
                    // live positions require auth and aren't available in this list).
                    total_stxoshi_value: (agent.total_deposited_stxoshi || 0) / 1_000_000,
                    total_stxoshi_yield: agent.total_yield_earned ?? agent.total_pnl ?? 0
                  };
                }
              } catch (err) {
                console.log(`Failed to fetch positions for agent ${agent.id}`);
              }
              return { 
                ...agent, 
                active_positions_count: 0,
                yield_earned: agent.total_yield_earned || 0,
                total_yield: agent.total_yield_earned || 0,
                total_stxoshi_value: (agent.total_deposited_stxoshi || 0) / 1_000_000,
                total_stxoshi_yield: agent.total_yield_earned ?? agent.total_pnl ?? 0
              };
            })
          );
          
          // Sort agents by yield earned (highest first), then by active positions
          const sortedAgents = agentsWithPositions.sort((a, b) => {
            const yieldDiff = (b.total_yield || 0) - (a.total_yield || 0);
            if (yieldDiff !== 0) return yieldDiff;
            return (b.active_positions_count || 0) - (a.active_positions_count || 0);
          });
          
          // Calculate total P&L across all agents
          const totalPnL = sortedAgents.reduce((sum, agent) => {
            return sum + (agent.total_pnl || agent.total_yield || 0);
          }, 0);
          
          // Update profile with calculated total P&L
          setProfile(prev => ({
            ...prev,
            total_pnl: totalPnL
          }));
          
          setAgents(sortedAgents);
        }
      } catch (agentErr) {
        console.log("Failed to fetch user's agents:", agentErr);
      }
      
      // Fetch user's bots
      try {
        const botsRes = await fetch(`${API}/prediction-bots`, {
          headers: { 'x-stx-address': profileRes.data.stx_address }
        });
        if (botsRes.ok) {
          const botsData = await botsRes.json();
          setBots(botsData.bots || []);
        }
      } catch (botErr) {
        console.log("Failed to fetch user's bots:", botErr);
      }

      // Fetch unified P&L summary (bots + perps) and long/short counts.
      // This is the source of truth for the stats row — the old
      // agents-only sum showed +0 for any user without agents.
      try {
        const pnlRes = await fetch(`${API}/users/${profileRes.data.stx_address}/pnl-summary`);
        if (pnlRes.ok) {
          const pnl = await pnlRes.json();
          setPnlSummary(pnl);
        }
      } catch (pnlErr) {
        console.log("Failed to fetch P&L summary:", pnlErr);
      }
    } catch (err) {
      console.error("Failed to fetch profile:", err);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!isConnected) {
      toast.error("Please connect your wallet");
      return;
    }
    try {
      if (isFollowing) {
        await authenticatedApi.unfollowUser(id);
        setIsFollowing(false);
        setProfile(p => ({ ...p, followers_count: p.followers_count - 1 }));
      } else {
        await authenticatedApi.followUser(id);
        setIsFollowing(true);
        setProfile(p => ({ ...p, followers_count: p.followers_count + 1 }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const handleSaveProfile = async () => {
    try {
      await updateUser(editData);
      setProfile(p => ({ ...p, ...editData }));
      setEditing(false);
      toast.success("Profile updated!");
    } catch (err) {
      toast.error("Failed to update profile");
    }
  };

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
    toast.success("Address copied!");
  };

  const handleProfilePictureClick = () => {
    if (isOwnProfile && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or WEBP.');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 2MB.');
      return;
    }

    setUploadingPicture(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/upload/profile-picture`, {
        method: 'POST',
        headers: {
          'X-STX-Address': addresses.stx
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await response.json();
      
      // Update local profile state
      setProfile(p => ({ ...p, avatar_url: data.avatar_url }));
      
      // Refresh user in context if available
      if (refreshUser) {
        await refreshUser();
      }
      
      toast.success('Profile picture updated!');
    } catch (err) {
      console.error('Profile picture upload error:', err);
      toast.error(err.message || 'Failed to upload profile picture');
    } finally {
      setUploadingPicture(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <span className="text-muted-foreground">Loading profile...</span>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <span className="text-muted-foreground">Profile not found</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Page Title with Back Button */}
      <div className="glass border-b border-border px-6 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <Badge variant="outline" className="font-mono tracking-wider">{profile.username}</Badge>
            <p className="text-xs text-muted-foreground mt-1">{posts.length} posts</p>
          </div>
        </div>
      </div>

      {/* Profile Header */}
      <div className="max-w-4xl mx-auto">
        {/* Banner */}
        <div className="h-32 bg-gradient-to-r from-primary/20 to-secondary/20" />
        
        {/* Avatar & Actions */}
        <div className="px-6 pb-4 border-b border-border">
          <div className="flex items-end justify-between -mt-12">
            {/* Clickable Avatar with Camera Overlay */}
            <div className="relative group">
              <Avatar 
                className={`w-24 h-24 border-4 border-background ${isOwnProfile ? 'cursor-pointer' : ''}`}
                onClick={handleProfilePictureClick}
              >
                <AvatarImage src={profile.avatar_url} />
                <AvatarFallback className="bg-primary/20 text-primary text-2xl font-heading">
                  {profile.username?.[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              
              {/* Camera overlay for own profile */}
              {isOwnProfile && (
                <div 
                  className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  onClick={handleProfilePictureClick}
                >
                  {uploadingPicture ? (
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  ) : (
                    <Camera className="w-6 h-6 text-white" />
                  )}
                </div>
              )}
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleProfilePictureUpload}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <SwapSTXOSHIButton className="h-9 text-xs" size="sm" />
              {isOwnProfile ? (
                <Button variant="outline" onClick={() => setEditing(!editing)}>
                  {editing ? <X className="w-4 h-4 mr-2" /> : <Edit className="w-4 h-4 mr-2" />}
                  {editing ? 'Cancel' : 'Edit Profile'}
                </Button>
              ) : (
                <Button 
                  onClick={handleFollow}
                  variant={isFollowing ? "outline" : "default"}
                  className={!isFollowing ? "bg-primary text-primary-foreground" : ""}
                >
                  {isFollowing ? 'Following' : 'Follow'}
                </Button>
              )}
            </div>
          </div>

          {/* Profile Info */}
          <div className="mt-4">
            {editing ? (
              <div className="space-y-3">
                <Input
                  placeholder="Username"
                  value={editData.username}
                  onChange={(e) => setEditData({ ...editData, username: e.target.value })}
                />
                <Textarea
                  placeholder="Bio"
                  value={editData.bio}
                  onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                />
                <Button onClick={handleSaveProfile} className="bg-primary text-primary-foreground">
                  <Check className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <h2 className="font-heading text-xl font-bold">{profile.username}</h2>
                  {profile.is_verified && (
                    <Badge className="bg-primary/20 text-primary">✓</Badge>
                  )}
                </div>
                <button 
                  className="text-sm text-muted-foreground font-mono flex items-center gap-1 hover:text-foreground"
                  onClick={() => copyAddress(profile.stx_address)}
                >
                  @{profile.stx_address?.slice(0, 12)}...{profile.stx_address?.slice(-4)}
                  <Copy className="w-3 h-3" />
                </button>
                {profile.bio && (
                  <p className="mt-2 text-foreground">{profile.bio}</p>
                )}
              </>
            )}

            {/* Stats */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 text-sm">
              <button 
                className="hover:underline"
                onClick={() => goToTab('following')}
                data-testid="profile-following-stat"
              >
                <span className="font-bold">{profile.following_count}</span>
                <span className="text-muted-foreground ml-1">Following</span>
              </button>
              <button 
                className="hover:underline"
                onClick={() => goToTab('followers')}
                data-testid="profile-followers-stat"
              >
                <span className="font-bold">{profile.followers_count}</span>
                <span className="text-muted-foreground ml-1">Followers</span>
              </button>
              <button 
                className="hover:underline"
                onClick={() => goToTab('agents')}
                data-testid="profile-agents-stat"
              >
                <span className="font-bold">{profile.agents_count}</span>
                <span className="text-muted-foreground ml-1">Agents</span>
              </button>
              <button 
                className="hover:underline"
                onClick={() => goToTab('bots')}
                data-testid="profile-bots-stat"
              >
                <span className="font-bold">{bots.length}</span>
                <span className="text-muted-foreground ml-1">Bots</span>
              </button>
              <button
                className="hover:underline"
                onClick={() => navigate('/perps')}
                data-testid="profile-long-perps"
              >
                <span className="font-bold text-green-400">
                  {pnlSummary?.long_open_perps ?? 0}
                </span>
                <span className="text-muted-foreground ml-1">Long</span>
              </button>
              <button
                className="hover:underline"
                onClick={() => navigate('/perps')}
                data-testid="profile-short-perps"
              >
                <span className="font-bold text-red-400">
                  {pnlSummary?.short_open_perps ?? 0}
                </span>
                <span className="text-muted-foreground ml-1">Short</span>
              </button>
              <div
                className={((pnlSummary?.total_pnl_stxoshi ?? 0) >= 0)
                  ? 'text-green-400'
                  : 'text-red-400'}
                data-testid="profile-total-pnl"
                title={`Bots ${(pnlSummary?.bot_pnl_stxoshi ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} · Perp realized ${(pnlSummary?.perp_realized_pnl_stxoshi ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} · Perp unrealized ${(pnlSummary?.perp_unrealized_pnl_stxoshi ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
              >
                <span className="font-bold font-mono">
                  {(pnlSummary?.total_pnl_stxoshi ?? 0) >= 0 ? '+' : ''}
                  {formatCompactPnl(pnlSummary?.total_pnl_stxoshi)}
                </span>
                <span className="text-muted-foreground ml-1">STXOSHI</span>
              </div>
              {profile.total_pnl != null && profile.total_pnl !== 0 && (
                <div className={profile.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}>
                  <span className="font-bold font-mono">
                    {profile.total_pnl >= 0 ? '+' : ''}{profile.total_pnl?.toFixed(6)}
                  </span>
                  <span className="text-muted-foreground ml-1">sBTC P&L</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" />
              Joined {new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </div>

            {isOwnProfile && activeTab === 'posts' && (
              <div className="mt-4 space-y-4">
                <DepositRecovery onRecovered={fetchProfile} />
                <UserWalletPanel />
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div ref={tabsAnchorRef} className="scroll-mt-20" />
        <Tabs value={activeTab} onValueChange={setActiveTab} className="px-6 py-4">
          <TabsList className="grid grid-cols-6 gap-1 w-full bg-muted/50">
            <TabsTrigger value="posts" className="font-heading tracking-tight text-[10px] sm:text-xs px-0.5 truncate">POSTS</TabsTrigger>
            <TabsTrigger value="agents" className="font-heading tracking-tight text-[10px] sm:text-xs px-0.5 truncate">AGENTS</TabsTrigger>
            <TabsTrigger value="bots" className="font-heading tracking-tight text-[10px] sm:text-xs px-0.5 truncate">BOTS</TabsTrigger>
            <TabsTrigger value="following" className="font-heading tracking-tight text-[10px] sm:text-xs px-0.5 truncate">FOLLOWING</TabsTrigger>
            <TabsTrigger value="followers" className="font-heading tracking-tight text-[10px] sm:text-xs px-0.5 truncate">FOLLOWERS</TabsTrigger>
            <TabsTrigger value="likes" className="font-heading tracking-tight text-[10px] sm:text-xs px-0.5 truncate">LIKES</TabsTrigger>
          </TabsList>

          <TabsContent value="posts" className="mt-4 space-y-4">
            {posts.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="p-12 text-center">
                  <p className="text-muted-foreground">No posts yet</p>
                </CardContent>
              </Card>
            ) : (
              posts.map(post => (
                <ProfilePostCard
                  key={post.id}
                  post={post}
                  isConnected={isConnected}
                  api={authenticatedApi}
                  onOpen={() => navigate(`/post/${post.id}`)}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="agents" className="mt-4 space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {isOwnProfile && profile?.stx_address && (
              <WalletHealth
                stxAddress={profile.stx_address}
                onHealed={fetchProfile}
              />
            )}
            {agents.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="p-12 text-center">
                  <Bot className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground mb-4">No agents yet</p>
                  {isOwnProfile && (
                    <Button onClick={() => navigate('/create')}>Create Agent</Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              [...agents].sort((a, b) => {
                const inactive = (s) => ['killed', 'stopped', 'paused', 'inactive'].includes((s || '').toLowerCase());
                const ak = inactive(a.status) ? 1 : 0;
                const bk = inactive(b.status) ? 1 : 0;
                if (ak !== bk) return ak - bk; // stopped/killed agents to the bottom
                const ap = Number(a.total_stxoshi_yield ?? a.total_defi_yield ?? a.total_stxoshi_value ?? 0);
                const bp = Number(b.total_stxoshi_yield ?? b.total_defi_yield ?? b.total_stxoshi_value ?? 0);
                return bp - ap; // highest STXOSHI P&L first
              }).map(agent => {
                const stxValue = Number(agent.total_stxoshi_value ?? agent.total_defi_value ?? 0);
                const stxYield = Number(agent.total_stxoshi_yield ?? agent.total_defi_yield ?? 0);
                const hasYield = stxYield !== 0;
                const hasPositions = agent.active_positions_count > 0;
                
                return (
                  <Card 
                    key={agent.id} 
                    className={`glass-card cursor-pointer hover:border-primary/50 ${hasPositions || hasYield ? 'border-green-500/30' : ''}`}
                    onClick={() => navigate(`/agent/${agent.id}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${hasPositions || hasYield ? 'bg-green-500/20' : 'bg-primary/20'}`}>
                            <Bot className={`w-5 h-5 ${hasPositions || hasYield ? 'text-green-400' : 'text-primary'}`} />
                          </div>
                          <div>
                            <h3 className="font-semibold">{agent.name}</h3>
                            <p className="text-xs text-muted-foreground">{agent.config?.archetype}</p>
                            {hasPositions && (
                              <p className="text-xs text-green-400 mt-1">
                                {agent.active_positions_count} active position{agent.active_positions_count > 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold" data-testid="agent-card-value">{stxValue.toLocaleString(undefined, { maximumFractionDigits: 2 })} STXOSHI</p>
                          <p className={`text-sm font-mono ${stxYield >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="agent-card-yield">
                            {stxYield >= 0 ? '+' : ''}{stxYield.toLocaleString(undefined, { maximumFractionDigits: 2 })} yield
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          <TabsContent value="bots" className="mt-4 space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {bots.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="p-12 text-center">
                  <Bot className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground mb-4">No bots yet</p>
                  {isOwnProfile && (
                    <Button onClick={() => navigate('/bots')} data-testid="profile-bots-empty-cta">Explore Bots</Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              [...bots].sort((a, b) => {
                const ak = (a.status || '').toLowerCase() === 'killed' ? 1 : 0;
                const bk = (b.status || '').toLowerCase() === 'killed' ? 1 : 0;
                if (ak !== bk) return ak - bk; // killed bots sink to the bottom
                return Number(b.total_pnl_pct || 0) - Number(a.total_pnl_pct || 0); // highest P&L first
              }).map(bot => {
                const pnlPct = Number(bot.total_pnl_pct ?? 0);
                const killed = (bot.status || '').toLowerCase() === 'killed';
                return (
                  <Card
                    key={bot.id}
                    className="glass-card cursor-pointer hover:border-primary/50"
                    onClick={() => navigate('/bots')}
                    data-testid="profile-bot-card"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-cyan-500/20 shrink-0">
                            <Bot className="w-5 h-5 text-cyan-400" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold truncate flex items-center gap-2">
                              {bot.name || bot.id}
                              {killed && <Badge variant="destructive" className="text-[9px]">KILLED</Badge>}
                            </h3>
                            <p className="text-xs text-muted-foreground truncate">
                              {(bot.status || 'active')} · {(bot.total_trades ?? 0)} trades
                            </p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className={`font-mono font-bold ${pnlPct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          <TabsContent value="following" className="mt-4 space-y-4">
            <Card className="glass-card">
              <CardContent className="p-12 text-center">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">
                  {profile.following_count > 0 
                    ? `Following ${profile.following_count} users` 
                    : 'Not following anyone yet'}
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="followers" className="mt-4 space-y-4">
            <Card className="glass-card">
              <CardContent className="p-12 text-center">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">
                  {profile.followers_count > 0 
                    ? `${profile.followers_count} followers` 
                    : 'No followers yet'}
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="likes" className="mt-4">
            <Card className="glass-card">
              <CardContent className="p-12 text-center">
                <p className="text-muted-foreground">Liked posts coming soon</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
